import { collection, getDocs } from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { AVAILABLE_VIEW_KEYS } from '../types/schema'
import type { Workspace } from '../types/workspace'
import { EXPLORE_WORKSPACE_SEEDS, type ExploreWorkspaceSeed } from '../explore_seeds/workspaceSeeds'
import { createDefaultWorkspaceLimits, limitsToChecklistInputLimits } from '../utils/workspaceLimits'
import { DEFAULT_WORKSPACE_ACTION_PERMISSIONS } from '../utils/workspacePermissions'
import type { WorkspaceActionPermissions } from '../utils/workspacePermissions'
import { normalizeChecklistBlockedTodoPolicy } from '../utils/checklistBlockedTodoPolicy'
import { WORKSPACE_SURFACE_MASK_PRESETS } from '../types/workspaceSurfaceMask'
import { isEduDemoWorkspace } from './eduDemo/eduDemoWorkspaces'

export const EXPLORE_PREVIEW_STORAGE_KEY = 'timelining-explore-preview-workspaces'
export const EXPLORE_PREVIEW_TTL_MS = 7 * 24 * 60 * 60 * 1000

type StoredExplorePreview = {
  seedId: string
  addedAt: number
}

const seedMap = new Map(EXPLORE_WORKSPACE_SEEDS.map((seed) => [seed.id, seed]))

const rememberLoadedSeeds = (seeds: ExploreWorkspaceSeed[]) => {
  seeds.forEach((seed) => seedMap.set(seed.id, seed))
}

const isSeed = (value: unknown): value is ExploreWorkspaceSeed => {
  if (!value || typeof value !== 'object') return false
  const seed = value as Partial<ExploreWorkspaceSeed>
  return typeof seed.id === 'string'
    && typeof seed.name === 'string'
    && typeof seed.slug === 'string'
    && Array.isArray(seed.elements)
    && Array.isArray(seed.checklistItems)
}

export const getFallbackExploreWorkspaceSeeds = () => EXPLORE_WORKSPACE_SEEDS

export const getExploreWorkspaceSeed = (seedId: string) => seedMap.get(seedId) ?? null

export const getExploreWorkspaceSeedByWorkspaceId = (workspaceId: string) => getExploreWorkspaceSeed(workspaceId)

export const loadExploreWorkspaceSeeds = async (): Promise<ExploreWorkspaceSeed[]> => {
  try {
    const snapshot = await getDocs(collection(firestoreDb, 'explore_seeds', 'workspaces', 'items'))
    const remoteSeeds = snapshot.docs
      .map((documentSnapshot) => documentSnapshot.data())
      .filter(isSeed)

    if (remoteSeeds.length > 0) {
      const bundledEduDemos = EXPLORE_WORKSPACE_SEEDS.filter((s) => isEduDemoWorkspace(s.id))
      const combined = [...remoteSeeds]
      for (const demo of bundledEduDemos) {
        if (!combined.some((s) => s.id === demo.id)) {
          combined.push(demo)
        }
      }
      rememberLoadedSeeds(combined)
      return combined
    }
    rememberLoadedSeeds(EXPLORE_WORKSPACE_SEEDS)
    return EXPLORE_WORKSPACE_SEEDS
  } catch (error) {
    console.warn('[Explore seeds] Falling back to bundled workspace seeds.', error)
    return EXPLORE_WORKSPACE_SEEDS
  }
}

const readStoredPreviews = (): StoredExplorePreview[] => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(EXPLORE_PREVIEW_STORAGE_KEY) ?? '[]')
    if (!Array.isArray(parsed)) return []
    return parsed
      .map((entry): StoredExplorePreview | null => {
        if (!entry || typeof entry !== 'object') return null
        const seedId = typeof entry.seedId === 'string' ? entry.seedId : ''
        const addedAt = typeof entry.addedAt === 'number' ? entry.addedAt : 0
        return seedId && addedAt ? { seedId, addedAt } : null
      })
      .filter((entry): entry is StoredExplorePreview => Boolean(entry))
  } catch {
    return []
  }
}

const pruneStoredPreviews = (entries: StoredExplorePreview[], now = Date.now()) =>
  entries.filter((entry) => now - entry.addedAt <= EXPLORE_PREVIEW_TTL_MS)

const writeStoredPreviews = (entries: StoredExplorePreview[]) => {
  window.localStorage.setItem(EXPLORE_PREVIEW_STORAGE_KEY, JSON.stringify(entries))
}

export const readRecentExplorePreviewSeeds = () => {
  const pruned = pruneStoredPreviews(readStoredPreviews())
  writeStoredPreviews(pruned)
  return pruned
    .map((entry) => getExploreWorkspaceSeed(entry.seedId))
    .filter((seed): seed is ExploreWorkspaceSeed => Boolean(seed))
}

export const rememberExplorePreviewSeed = (seedId: string) => {
  const now = Date.now()
  const current = pruneStoredPreviews(readStoredPreviews(), now)
  const existing = current.find((entry) => entry.seedId === seedId)
  const next = existing
    ? current.map((entry) => entry.seedId === seedId ? entry : { ...entry })
    : [{ seedId, addedAt: now }, ...current]
  writeStoredPreviews(next.slice(0, 10))
}

export const toPreviewWorkspace = (seed: ExploreWorkspaceSeed): Workspace => {
  const workspaceLimits = createDefaultWorkspaceLimits(false)
  const actionPermissions = Object.fromEntries(
    Object.entries(DEFAULT_WORKSPACE_ACTION_PERMISSIONS).map(([action, minimumRole]) => [
      action,
      action === 'workspace.view_settings'
        || action === 'workspace.view_reports'
        || action.startsWith('readonly.view_')
        || action === 'timeline.view'
        || action === 'overview.view'
        || action === 'forms.view'
        || action === 'meetings.view'
        || action === 'automation.view'
        || action === 'chat.view'
        || action === 'integrations.view'
        ? 'READONLY'
        : minimumRole,
    ]),
  ) as WorkspaceActionPermissions

  return {
    id: seed.id,
    name: seed.name,
    slug: seed.slug,
    ownerId: 'explore-seeds',
    kind: 'TEAM',
    credits: 0,
    plan: 'PROFESSIONAL_TIER',
    subscription: { status: 'ACTIVE', periodEnd: null, providerSubscriptionId: '' },
    limits: { workspacesCount: 1, storageUsedBytes: 0, lastResetAt: null },
    timezone: isEduDemoWorkspace(seed.id) ? 'Asia/Kolkata' : Intl.DateTimeFormat().resolvedOptions().timeZone,
    teamBalanceCredits: 0,
    isPersonal: false,
    isPublic: true,
    checklistInputLimits: limitsToChecklistInputLimits(workspaceLimits.softCaps),
    workspaceLimits,
    enabledDashboardViews: [...AVAILABLE_VIEW_KEYS],
    defaultDashboardView: 'TIMELINE',
    actionPermissions,
    auditSettings: {
      retentionMode: 'FULL',
      fullAuditEnabled: true,
      lifecycleAlertEnabled: true,
      enabledCategories: ['CONTENT_CHANGES', 'ROLE_CHANGES', 'BILLING'],
    },
    cardBackgroundSettings: { enabled: true },
    checklistBlockedTodoPolicy: normalizeChecklistBlockedTodoPolicy(),
    modules: {
      stockEnabled: true,
      budgetEnabled: true,
      budgetEnforcement: 'warn',
      quotaEnabled: true,
      diagnosticsEnabled: false,
      ...(isEduDemoWorkspace(seed.id)
        ? {
            teachingEnabled: seed.id === 'school-demo' || seed.id === 'coaching-demo',
            trainingEnabled: seed.id === 'sports-academy-demo',
          }
        : {}),
    },
    settings: {
      currency: seed.currency ?? (isEduDemoWorkspace(seed.id) ? 'INR' : undefined),
    },
    isPreview: true,
    previewKind: 'DEMO',
    previewSource: 'EXPLORE_SEED',
    previewSurfaceMask: WORKSPACE_SURFACE_MASK_PRESETS.LEARN.mask,
    isFullDemo: seed.id === 'explore-seed-aria-and-co',
    previewCover: {
      tagline: seed.summary,
      category: seed.tags[0] ?? 'Demo',
      imagePath: seed.coverImagePath,
      heroGradient: seed.heroGradient,
      tags: seed.tags,
      featured: seed.featured === true,
      verified: seed.verified === true,
    },
  }
}
